import React, { Component, ReactElement, ReactNode } from 'react';
import { getProduct } from '../api/shared-app-requests';
import { logError } from '../utils/logging';
import { Offer, Product, SelectedProduct } from '../types';
import { createContext } from '../dissolve/utils';

type ProductsContextShape = {
  loadProduct: (handle: string) => Promise<Product>;
  removeOffersWithUnavailableProducts: (offers: Offer[]) => Promise<Offer[]>;
  noProductOffersAvailable: boolean;
  setSelectedProduct: (product: SelectedProduct | undefined) => void;
  resetSelectedProduct: () => void;
  selectedProduct?: SelectedProduct;
  productDetail: ReactNode;
  setProductDetail: (component: ReactNode) => void;
};

const [useProductsContext, ContextProvider, ContextConsumer] =
  createContext<ProductsContextShape>();

function productOfferVariants(offer: Offer, product?: Product) {
  if (!product || !product.variants) {
    return [];
  }
  if (offer.shopify_product_variants?.length > 0) {
    return product.variants.filter((v) =>
      offer.shopify_product_variants.includes(v.id),
    );
  }
  return product.variants;
}

function productOfferValid(offer: Offer, product?: Product) {
  if (offer.use_recommended_products) return true;
  return (
    product &&
    product.available &&
    productOfferVariants(offer, product).find((v) => v.available)
  );
}

interface ProductsContextProviderState {
  products: {
    [handle: string]: {
      promise: Promise<any>;
      loaded: boolean;
      data?: Product;
    };
  };
  context: ProductsContextShape;
}

interface Props {
  children: React.ReactNode;
}

class ProductsContextProvider extends Component<
  Props,
  ProductsContextProviderState
> {
  constructor(props: Props) {
    super(props);
    this.loadProduct = this.loadProduct.bind(this);
    this.removeOffersWithUnavailableProducts =
      this.removeOffersWithUnavailableProducts.bind(this);
    this.setProduct = this.setProduct.bind(this);
    this.setSelectedProduct = this.setSelectedProduct.bind(this);
    this.state = {
      products: {},
      context: {
        selectedProduct: undefined,
        setSelectedProduct: this.setSelectedProduct,
        loadProduct: this.loadProduct,
        removeOffersWithUnavailableProducts:
          this.removeOffersWithUnavailableProducts,
        resetSelectedProduct: () => {
          this.setSelectedProduct(undefined);
          this.state.context.setProductDetail(null);
        },
        productDetail: null,
        setProductDetail: (component: ReactElement) => {
          this.setState(({ context }) => ({
            context: { ...context, productDetail: component },
          }));
        },
        noProductOffersAvailable: false,
      },
    } as ProductsContextProviderState;
  }

  async loadProduct(handle: string) {
    if (this.state.products[handle]) {
      return this.state.products[handle].promise;
    }

    try {
      const productPromise = getProduct(handle);

      this.setProduct(handle, {
        loading: true,
        promise: productPromise,
      });

      const product = await productPromise;

      if (!product) {
        console.warn(`handle ${handle} was not found.`);
        this.setProduct(handle, { loaded: true, available: false });
        return;
      }

      product.promise = productPromise;
      product.loaded = true;

      // It's fine that recommended product has empty handle
      this.setProduct(handle, product);
      return product;
    } catch (error) {
      logError('Failed to load product, setting as not available.', error);
      this.setProduct(handle, { loaded: true, available: false });
    }
  }

  checkNoProductOffersAvailable = () => {
    const { products } = this.state;

    const noProductOffersAvailable =
      Object.keys(products)?.length > 0 &&
      Object.values(products).every(
        (product) => product && product.loaded && !(product as any).available,
      );

    this.setState((prevState: ProductsContextProviderState) => {
      return {
        ...prevState,
        context: {
          ...prevState.context,
          noProductOffersAvailable,
        },
      };
    });
  };

  setProduct = (handle: string, data: any) => {
    this.setState(
      (prevState: ProductsContextProviderState) => {
        return {
          ...prevState,
          products: {
            ...prevState.products,
            [handle]: data,
          },
        };
      },
      () => this.checkNoProductOffersAvailable(),
    );
  };

  setSelectedProduct(product: SelectedProduct | undefined) {
    this.setState(({ context }) => ({
      context: { ...context, selectedProduct: product },
    }));
  }

  async removeOffersWithUnavailableProducts(offers: Offer[]) {
    await Promise.all([
      ...offers
        .filter((offer) => offer.is_product && offer.shopify_product_handle)
        .map((offer) => this.loadProduct(offer.shopify_product_handle)),
    ]);

    return offers.filter((offer) => {
      if (!offer.is_product) return true;

      let productToValidate = this.state.products[
        offer.shopify_product_handle
      ] as unknown as Product;

      if (offer.is_same_product_offer) {
        productToValidate = offer.parentProduct || productToValidate;
      }

      return productOfferValid(offer, productToValidate);
    });
  }

  render() {
    const { context } = this.state;

    return (
      <ContextProvider
        value={{
          ...context,
        }}
      >
        {this.props.children}
      </ContextProvider>
    );
  }
}

const ProductsContextConsumer =
  ContextConsumer as React.Consumer<ProductsContextShape>;

export {
  useProductsContext,
  ProductsContextConsumer,
  ProductsContextProvider,
  productOfferVariants,
};
